/**
 * Service to detect cascading 404 errors that indicate stale tokens
 * and automatically force logout to prevent infinite loading states
 */

interface ErrorCount {
  count: number
  lastErrorTime: number
}

export class StaleTokenDetectorService {
  private static instance: StaleTokenDetectorService
  private errorCounts: Map<string, ErrorCount> = new Map()

  // Configuration
  private readonly max404Count = 10 // Max consecutive 404s before logout
  private readonly max404TimeWindow = 30000 // 30 seconds window
  private readonly maxTotal404Count = 50 // Max total 404s in any 5-minute window
  private readonly maxTotal404TimeWindow = 300000 // 5 minutes

  private constructor() {
    // Clean up old error counts periodically
    setInterval(() => {
      this.cleanupOldErrors()
    }, 60000) // Clean up every minute
  }

  public static getInstance(): StaleTokenDetectorService {
    if (!StaleTokenDetectorService.instance) {
      StaleTokenDetectorService.instance = new StaleTokenDetectorService()
    }
    return StaleTokenDetectorService.instance
  }

  /**
   * Record a 404 error and check if we should force logout
   * @param endpoint The API endpoint that returned 404
   * @returns true if logout should be triggered, false otherwise
   */
  public record404Error(endpoint: string): boolean {
    const currentTime = Date.now()
    const errorKey = this.getErrorKey(endpoint)

    let errorCount = this.errorCounts.get(errorKey)
    if (!errorCount) {
      errorCount = { count: 0, lastErrorTime: 0 }
    }

    // Check if this is within the time window for consecutive errors
    if (currentTime - errorCount.lastErrorTime > this.max404TimeWindow) {
      errorCount.count = 1 // Reset count if outside time window
    } else {
      errorCount.count++
    }
    errorCount.lastErrorTime = currentTime

    this.errorCounts.set(errorKey, errorCount)

    // Check for consecutive 404s on the same endpoint
    if (errorCount.count >= this.max404Count) {
      console.error(
        `Detected ${errorCount.count} consecutive 404 errors on ${endpoint} within ${this.max404TimeWindow}ms`,
      )
      return true
    }

    // Check for total 404s across all endpoints
    const total404s = this.getTotal404CountInWindow()
    if (total404s >= this.maxTotal404Count) {
      console.error(
        `Detected ${total404s} total 404 errors across all endpoints within ${this.maxTotal404TimeWindow}ms`,
      )
      return true
    }

    return false
  }

  /**
   * Reset error counts (call on successful requests)
   */
  public resetErrorCounts(): void {
    this.errorCounts.clear()
  }

  /**
   * Force logout when stale tokens are detected
   */
  public forceLogout(): void {
    console.warn('Force logout triggered due to stale token detection - clearing tokens and reloading')

    // Clear all tokens
    localStorage.removeItem('token')
    localStorage.removeItem('refresh_token')

    // Clear any cached data that might be causing the cascade
    if (window.location.pathname !== '/login') {
      // Only reload if not already on login page
      location.reload()
    }
  }

  private getErrorKey(endpoint: string): string {
    // Normalize endpoint to group similar requests
    return endpoint.replace(/\/\d+/g, '/:id') // Replace IDs with placeholder
  }

  private getTotal404CountInWindow(): number {
    const currentTime = Date.now()
    let totalCount = 0

    for (const [_, errorCount] of this.errorCounts) {
      if (currentTime - errorCount.lastErrorTime <= this.maxTotal404TimeWindow) {
        totalCount += errorCount.count
      }
    }

    return totalCount
  }

  private cleanupOldErrors(): void {
    const currentTime = Date.now()
    const cutoffTime = currentTime - this.maxTotal404TimeWindow

    for (const [key, errorCount] of this.errorCounts) {
      if (errorCount.lastErrorTime < cutoffTime) {
        this.errorCounts.delete(key)
      }
    }
  }
}
